import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Globe, Loader2, X, Home, Pencil, Check } from 'lucide-react';
import { apiRequest } from '../../../config/api';

/**
 * PagesTab - Site integration for page builder
 *
 * Shows either:
 * - Standalone page view with option to create a site
 * - Site pages list with navigation, homepage toggle, and slug editing
 */
export default function PagesTab({ page, onRefreshPage }) {
  const navigate = useNavigate();

  // State management
  const [siteInfo, setSiteInfo] = useState(null);
  const [sitePages, setSitePages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateSiteModal, setShowCreateSiteModal] = useState(false);
  const [showAddPageModal, setShowAddPageModal] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [newPageTitle, setNewPageTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingSlugId, setEditingSlugId] = useState(null);
  const [editingSlugValue, setEditingSlugValue] = useState('');

  // Fetch site data when page changes
  const fetchSiteData = useCallback(async () => {
    if (!page?.site_id) {
      setSiteInfo(null);
      setSitePages([]);
      return;
    }

    setIsLoading(true);
    try {
      // Fetch site details and pages in parallel
      const [siteResponse, pagesResponse] = await Promise.all([
        apiRequest(`/sites/${page.site_id}`),
        apiRequest(`/sites/${page.site_id}/pages`)
      ]);

      if (siteResponse.ok) {
        const siteData = await siteResponse.json();
        setSiteInfo(siteData);
      }

      if (pagesResponse.ok) {
        const pagesData = await pagesResponse.json();
        setSitePages(pagesData);
      }
    } catch (error) {
      console.error('Error fetching site data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page?.site_id]);

  useEffect(() => {
    fetchSiteData();
  }, [fetchSiteData]);

  // Create site handler
  const handleCreateSite = async (e) => {
    e.preventDefault();
    if (!newSiteName.trim() || creating) return;

    setCreating(true);
    try {
      // Create the site
      const createResponse = await apiRequest('/sites/', {
        method: 'POST',
        body: JSON.stringify({ name: newSiteName.trim() })
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create site');
      }

      const newSite = await createResponse.json();

      // Associate current page with the new site and set as homepage
      const updateResponse = await apiRequest(`/pages/${page.id}`, {
        method: 'PUT',
        body: JSON.stringify({ site_id: newSite.id, is_homepage: true })
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to associate page with site');
      }

      // Refresh page data and close modal
      if (onRefreshPage) {
        await onRefreshPage();
      }

      setShowCreateSiteModal(false);
      setNewSiteName('');
    } catch (error) {
      console.error('Error creating site:', error);
      alert('Failed to create site. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  // Add page handler
  const handleAddPage = async (e) => {
    e.preventDefault();
    if (!newPageTitle.trim() || creating || !siteInfo) return;

    setCreating(true);
    try {
      const hasHomepage = sitePages.some(p => p.is_homepage);
      const response = await apiRequest('/pages', {
        method: 'POST',
        body: JSON.stringify({
          title: newPageTitle.trim(),
          site_id: siteInfo.id,
          page_type: 'landing',
          content_type: 'tff_page_builder_json_simple',
          content: {},
          is_homepage: !hasHomepage,
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create page');
      }

      // Refetch site pages
      await fetchSiteData();

      setShowAddPageModal(false);
      setNewPageTitle('');
    } catch (error) {
      console.error('Error adding page:', error);
      alert('Failed to add page. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  // Toggle homepage
  const handleToggleHomepage = async (e, targetPage) => {
    e.stopPropagation();
    const newValue = !targetPage.is_homepage;
    try {
      const response = await apiRequest(`/pages/${targetPage.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_homepage: newValue })
      });
      if (response.ok) {
        await fetchSiteData();
      }
    } catch (error) {
      console.error('Error toggling homepage:', error);
    }
  };

  // Save slug
  const handleSaveSlug = async (targetPageId) => {
    const cleanSlug = editingSlugValue.toLowerCase().replace(/^\/+|\/+$/g, '');
    if (!cleanSlug) {
      setEditingSlugId(null);
      return;
    }
    try {
      const response = await apiRequest(`/pages/${targetPageId}`, {
        method: 'PUT',
        body: JSON.stringify({ slug: cleanSlug })
      });
      if (response.ok) {
        await fetchSiteData();
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to update slug');
      }
    } catch (error) {
      console.error('Error saving slug:', error);
    } finally {
      setEditingSlugId(null);
    }
  };

  // Navigate to page builder
  const handleNavigateToPage = (pageId) => {
    if (pageId !== page?.id) {
      navigate(`/pages/${pageId}/builder`);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  // Scenario A: Standalone page (no site_id)
  if (!page?.site_id) {
    return (
      <>
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <FileText className="w-6 h-6 text-gray-400" />
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">
            This page is standalone
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            Create a site to organize multiple pages together
          </p>
          <button
            onClick={() => setShowCreateSiteModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors"
          >
            <Globe className="w-4 h-4" />
            Create Site
          </button>
        </div>

        {/* Create Site Modal */}
        {showCreateSiteModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={(e) => e.target === e.currentTarget && setShowCreateSiteModal(false)}
          >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">Create Site</h2>
                <button
                  onClick={() => setShowCreateSiteModal(false)}
                  className="p-1 hover:bg-gray-200 rounded-md transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <form onSubmit={handleCreateSite} className="p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Site Name
                </label>
                <input
                  type="text"
                  value={newSiteName}
                  onChange={(e) => setNewSiteName(e.target.value)}
                  placeholder="My Website"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  autoFocus
                />
                <p className="mt-2 text-xs text-gray-500">
                  This page will be added to the new site
                </p>

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateSiteModal(false)}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!newSiteName.trim() || creating}
                    className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                    Create Site
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </>
    );
  }

  // Scenario B: Page belongs to a site
  return (
    <>
      <div className="flex flex-col h-full">
        {/* Site Header */}
        <div className="px-3 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-brand-600" />
            <span className="text-sm font-medium text-gray-900">
              {siteInfo?.name || 'Site'}
            </span>
          </div>
        </div>

        {/* Pages List */}
        <div className="flex-1 overflow-auto">
          <div className="divide-y divide-gray-100">
            {sitePages.map((p) => (
              <div
                key={p.id}
                className={`px-3 py-2 ${p.id === page.id ? 'bg-brand-50' : ''}`}
              >
                {/* Line 1: Home icon | Title | (current) */}
                <div
                  className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-50 rounded -mx-1 px-1"
                  onClick={() => handleNavigateToPage(p.id)}
                >
                  <button
                    onClick={(e) => handleToggleHomepage(e, p)}
                    className={`p-0.5 rounded transition-colors flex-shrink-0 ${
                      p.is_homepage
                        ? 'text-amber-500 hover:text-amber-600'
                        : 'text-gray-300 hover:text-gray-400'
                    }`}
                    title={p.is_homepage ? 'Homepage' : 'Set as homepage'}
                  >
                    <Home className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-sm text-gray-700 flex-1 truncate">
                    {p.title}
                  </span>
                  {p.id === page.id && (
                    <span className="text-xs text-brand-600 flex-shrink-0">(current)</span>
                  )}
                </div>

                {/* Line 2: Slug display / edit */}
                <div className="ml-6 mt-0.5">
                  {editingSlugId === p.id ? (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-400">/</span>
                      <input
                        type="text"
                        value={editingSlugValue}
                        onChange={(e) => setEditingSlugValue(e.target.value.toLowerCase().replace(/[^a-z0-9\-/]/g, ''))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveSlug(p.id);
                          if (e.key === 'Escape') setEditingSlugId(null);
                        }}
                        className="flex-1 min-w-0 text-xs px-1 py-0.5 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        autoFocus
                      />
                      <button
                        onClick={() => handleSaveSlug(p.id)}
                        className="p-0.5 text-blue-600 hover:text-blue-700 rounded"
                      >
                        <Check className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => setEditingSlugId(null)}
                        className="p-0.5 text-gray-400 hover:text-gray-500 rounded"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 group">
                      <span className="text-xs text-gray-400 truncate">
                        /{p.slug}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSlugId(p.id);
                          setEditingSlugValue(p.slug);
                        }}
                        className="p-0.5 text-gray-300 hover:text-gray-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  {/* URL preview while editing */}
                  {editingSlugId === p.id && siteInfo?.subdomain && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {siteInfo.subdomain}.fasttttt.com/{editingSlugValue}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Add Page Button */}
        <div className="px-3 py-3 border-t">
          <button
            onClick={() => setShowAddPageModal(true)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Page
          </button>
        </div>
      </div>

      {/* Add Page Modal */}
      {showAddPageModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowAddPageModal(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900">Add Page</h2>
              <button
                onClick={() => setShowAddPageModal(false)}
                className="p-1 hover:bg-gray-200 rounded-md transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <form onSubmit={handleAddPage} className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Page Title
              </label>
              <input
                type="text"
                value={newPageTitle}
                onChange={(e) => setNewPageTitle(e.target.value)}
                placeholder="New Page"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                autoFocus
              />
              <p className="mt-2 text-xs text-gray-500">
                The page will be added to "{siteInfo?.name}"
              </p>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddPageModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newPageTitle.trim() || creating}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                  Add Page
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
